import React, { useEffect, useRef, useState } from "react";
import PortLocation, { PortLocationRef } from "kontrolEdge/components/Port/AddService/PortLocation";
import PortConfiguration, { PortConfigurationRef } from "kontrolEdge/components/Port/AddService/PortConfiguration";
import ServiceOverview from "kontrolEdge/components/Port/AddService/ServiceOverview";
import OrderOverview from "kontrolEdge/components/Port/AddService/OrderOverview";
import OrderOverviewConfirmation from "kontrolEdge/components/Port/AddService/OrderOverviewConfirmation";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericStepper from "@ui-components/Stepper/GenericStepper";

//Icons
import { ReactComponent as CheckIcon } from "../../shared/assets/check.svg";
import { ReactComponent as IconLeft } from "../../shared/assets/left.svg";
import { ReactComponent as IconRight } from "../../shared/assets/right.svg";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { createPortAsync, fetchLocations, loadSavedOrder, resetOrder, setCurrentStep } from "@redux/slices/kontrolEdge/orderSlice";
import { useAuth0 } from "@auth0/auth0-react";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "@redux/store/store";
import Summary from "kontrolEdge/components/Port/AddService/Summary";


const PortCreationFlow: React.FC = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const currentStep = useAppSelector((state: RootState) => state.order.currentStep);

  // Alert state variables for dynamic alert configuration
  const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertVariant, setAlertVariant] = useState<"success" | "error" | "confirm" | undefined>(undefined);
  const [token, setToken] = useState<string>("");
  const location = useLocation();


  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    
    const fetchData = async () => {
      try {
        const fetchedToken = await getAccessTokenSilently();
        setToken(fetchedToken)
        dispatch(fetchLocations(fetchedToken));
      } catch (error) {
        console.error("Error fetching locations", error);
      }
    };
    fetchData();
  }, [dispatch, isLoading, isAuthenticated, getAccessTokenSilently]);

  const steps = [
    { title: "Port price" },
    { title: "Port configuration" },
    { title: "Port overview" },
  ].map((step, index) => {
    const stepNumber = index + 1;
    let status: "completed" | "active" | "disabled";
    if (stepNumber < currentStep) {
      status = "completed";
    } else if (stepNumber === currentStep) {
      status = "active";
    } else {
      status = "disabled";
    }
    return {
      ...step,
      status,
      icon: status === "completed" ? <CheckIcon className="h-4 w-4" /> : undefined,
    };
  });

  // Create refs for steps that have a submit handler.
  const portLocationRef = useRef<PortLocationRef>(null);
  const portConfigurationRef = useRef<PortConfigurationRef>(null);


useEffect(() => {
  if (location.state?.order) {
    // Existing order: editing mode
    dispatch(loadSavedOrder({ ...location.state.order, isEditing: true }));
    dispatch(setCurrentStep(location.state.currentStep));
  } else {
    // New order: reset to initial state
    dispatch(resetOrder());
  }
}, [location, dispatch]);


  const handleNext = async () => {
    if (currentStep === 1) {
      if (portLocationRef.current) {
        const isValid = await portLocationRef.current.submit();
        if (isValid) {
          dispatch(setCurrentStep(currentStep + 1));
        }
      } else {
        console.warn("Submit handler is not set for this step");
      }
    } else if (currentStep === 2) {
      if (portConfigurationRef.current) {
        const isValid = await portConfigurationRef.current.submit();
        if (isValid) {
          dispatch(setCurrentStep(currentStep + 1));
        }
      } else {
        console.warn("Submit handler is not set for this step");
      }
    } else {
      dispatch(setCurrentStep(currentStep + 1));
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      dispatch(setCurrentStep(currentStep - 1));
    }
  };

  const handleSubmit = async () => {
    try {
      const token = await getAccessTokenSilently();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await dispatch(createPortAsync(token)).unwrap();
      // console.log("Port created successfully:", response.payload);
      setAlertTitle("New Service is Active");
      setAlertDescription("Thank you for choosing us as your connectivity provider. Your new services have been successfully ordered and the provisioning process will begin immediately. If we require additional information to complete the activation, we will notify you by email. You can track the progress of your service provisioning in this section:");
      setAlertVariant("success");
      setIsResultAlertOpen(true);
    } catch (error) {
      console.error("Error creating port:", error);
      setAlertTitle("Error");
      setAlertDescription("There was an error saving your order. Please try again.");
      setAlertVariant("error");
      setIsResultAlertOpen(true);
    }
  };
  

  const headerTitles = [
    "Port Price",
    "Port Configuration",
    "Port Overview",
    "Order Overview",
    "Review our terms and conditions",
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <PortLocation ref={portLocationRef} token={token}/>;
      case 2:
        return <PortConfiguration ref={portConfigurationRef} token={token} />;
      case 3:
        return <ServiceOverview />;
      case 4:
        return <OrderOverview token={token} currentStep={currentStep} />;
      case 5:
        return <OrderOverviewConfirmation onAcceptChange={setTermsAccepted} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 flex flex-col items-left">
        <GenericAlert
            isOpen={isResultAlertOpen}
            onClose={() => setIsResultAlertOpen(false)}
            onConfirm={() => {
              setIsResultAlertOpen(false);
              navigate("/kontrol/port");
            }}
            title={alertTitle}
            description={alertDescription}
            cancelLabel="Close"
            confirmLabel="Go to port"
            variant={alertVariant}
        />
      {currentStep < 4 && (
        <GenericStepper
          steps={steps}
          orientation="horizontal"
      />
      )}
      <div className="mt-8 rounded-md border border-gray-300 bg-semantic-bg-primary ">
      <h2 className="text-lg px-6 py-4 font-bold text-semantic-text-primary text-left border-b border-gray-300">
        {headerTitles[currentStep - 1]}
      </h2>

        {renderStep()}
        {/* Summary Section */}
        {currentStep <= 3 && <Summary currentStep={currentStep} token={token} />}
        <div className="mt-6 px-6 pb-4 flex justify-between w-full">
          <GenericButton
            label="Back"
            variant="secondary"
            onClick={handleBack}
            icon={<IconLeft className="w-4"/>}
            disabled={currentStep === 1 ? true : false}
          />
          {currentStep < 5 ? (
          <GenericButton
            label="Next"
            variant="brand"
            iconPosition="right"
            icon={<IconRight className="w-4"/>}
            onClick={handleNext}
            
          />
          ) : (
          <GenericButton
            label="Accept and place order"
            variant="brand"
            type="submit"
            onClick={handleSubmit}
            disabled={!termsAccepted}
          />
          )}
        </div>
      </div>
    </div>
  );
};

export default PortCreationFlow;
